import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Stack, Chip, LinearProgress, Skeleton } from '@mui/material';
import { PlayArrow, CheckCircle, AccessTime } from '@mui/icons-material';
import { getCustomerVideo } from '../../Services/videoService';
import { OPEN_VIDEOS_EVENT, VIDEOS_AVAILABLE_EVENT } from './LeadVideoNudge';

// Per-video blurb. Keyed by sequence so re-ordering the table does not
// mis-assign the text. Falls back to nothing if a new video appears.
const DESCRIPTIONS = {
  1: 'A short introduction to what happens next now that your booking is confirmed, and who will be looking after you.',
  2: 'How to read your proposed cost sheet: what each line means, what is already paid, and what is still due.',
  3: 'Your home loan options, which banks we work with, and what paperwork to keep ready.',
};

function clock(seconds) {
  if (!seconds && seconds !== 0) return null;
  const total = Math.round(Number(seconds));
  if (!Number.isFinite(total) || total <= 0) return null;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m + ':' + String(s).padStart(2, '0');
}

/**
 * Tutorials — a normal page listing the onboarding videos with their details.
 *
 * Playback still happens in the shared modal (LeadVideoNudge owns it), so all
 * the watch-history tracking stays in one place. Clicking a card just asks that
 * modal to open on a specific video.
 */
const TutorialsPage = () => {
  const loggedInUser = useSelector((state) => state.Auth.user);
  const leadId = loggedInUser && loggedInUser.leadId;

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!leadId) {
      setVideos([]);
      setLoading(false);
      return;
    }
    const result = await getCustomerVideo(leadId);
    setVideos(result && result.videos ? result.videos : []);
    setLoading(false);
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  // The modal refreshes the series when it closes; pick that up so the cards
  // show new progress without the customer reloading the page.
  useEffect(() => {
    const onChanged = () => load();
    window.addEventListener(VIDEOS_AVAILABLE_EVENT, onChanged);
    return () => window.removeEventListener(VIDEOS_AVAILABLE_EVENT, onChanged);
  }, [load]);

  const play = (sequence) => {
    window.dispatchEvent(
      new CustomEvent(OPEN_VIDEOS_EVENT, { detail: { sequence: sequence } })
    );
  };

  const watched = videos.filter((v) => v.fullyWatched).length;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#101828' }}>
        Tutorials
      </Typography>
      <Typography sx={{ fontSize: 14, color: '#667085', mt: 0.5 }}>
        Short videos explaining your booking, your cost sheet and your loan options.
      </Typography>

      {!loading && videos.length > 0 && (
        <Chip
          size="small"
          label={
            watched === videos.length
              ? 'All ' + videos.length + ' watched'
              : watched + ' of ' + videos.length + ' watched'
          }
          sx={{
            mt: 1.5,
            fontFamily: 'Inter-Medium',
            fontWeight: 700,
            fontSize: 11.5,
            color: watched === videos.length ? '#1e7b45' : '#b25e09',
            backgroundColor: watched === videos.length ? '#e7f6ed' : '#fdf0e3',
          }}
        />
      )}

      {loading && (
        <Stack spacing={2} sx={{ mt: 3 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={132} />
          ))}
        </Stack>
      )}

      {!loading && videos.length === 0 && (
        <Box
          sx={{
            mt: 4, p: 4, textAlign: 'center',
            border: '1px dashed #d0d5dd', borderRadius: 2, backgroundColor: '#fcfcfd',
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#344054' }}>
            No tutorials available yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#667085', mt: 0.5 }}>
            They will appear here once your booking reaches the right stage.
          </Typography>
        </Box>
      )}

      <Stack spacing={2} sx={{ mt: 3, pb: 3 }}>
        {videos.map((v) => {
          const done = !!v.fullyWatched;
          const pct = Number(v.progress && v.progress.watchPercentage) || 0;
          const length = clock(v.progress && v.progress.videoDuration);

          return (
            <Box
              key={v.id}
              role="button"
              tabIndex={0}
              onClick={() => play(v.sequence)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  play(v.sequence);
                }
              }}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 },
                p: 1.5,
                border: '1px solid #e4e7ec',
                borderRadius: 2,
                backgroundColor: '#fff',
                cursor: 'pointer',
                transition: 'box-shadow .15s, border-color .15s',
                '&:hover': { borderColor: '#1570ef', boxShadow: '0 4px 14px rgba(16,24,40,.08)' },
                '&:focus-visible': { outline: '2px solid #1570ef', outlineOffset: 2 },
              }}
            >
              {/* Thumbnail: a frame from the video itself. #t=2 asks the browser
                  for the frame at 2s, which avoids a black opening frame. */}
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: '100%', sm: 208 },
                  minWidth: { sm: 208 },
                  height: { xs: 168, sm: 117 },
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  backgroundColor: '#000',
                }}
              >
                <video
                  src={v.videoUrl + '#t=2'}
                  preload="metadata"
                  muted
                  playsInline
                  tabIndex={-1}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <Box
                  sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.25)',
                  }}
                >
                  <PlayArrow sx={{ color: '#fff', fontSize: 40 }} />
                </Box>

                {length && (
                  <Box
                    sx={{
                      position: 'absolute', right: 6, bottom: 6,
                      px: 0.75, py: '1px', borderRadius: '3px',
                      backgroundColor: 'rgba(0,0,0,0.8)', color: '#fff',
                      fontSize: 11, fontFamily: 'Inter-Medium', lineHeight: 1.6,
                    }}
                  >
                    {length}
                  </Box>
                )}

                {pct > 0 && !done && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, pct)}
                    sx={{
                      position: 'absolute', left: 0, right: 0, bottom: 0, height: 4,
                      backgroundColor: 'rgba(255,255,255,0.35)',
                      '& .MuiLinearProgress-bar': { backgroundColor: '#e67e22' },
                    }}
                  />
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#101828' }}>
                    {v.sequence}. {v.title}
                  </Typography>
                  {done && <CheckCircle sx={{ color: '#1e7b45', fontSize: 18 }} />}
                </Stack>

                <Typography sx={{ fontSize: 13, color: '#475467', lineHeight: 1.55 }}>
                  {DESCRIPTIONS[v.sequence] || ''}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  {done ? (
                    <Chip
                      size="small"
                      label="Watched"
                      icon={<CheckCircle sx={{ fontSize: 15 }} />}
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 700,
                        color: '#1e7b45', backgroundColor: '#e7f6ed',
                        '& .MuiChip-icon': { color: '#1e7b45' },
                      }}
                    />
                  ) : pct > 0 ? (
                    <Chip
                      size="small"
                      label={pct + '% watched'}
                      icon={<AccessTime sx={{ fontSize: 15 }} />}
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 700,
                        color: '#b25e09', backgroundColor: '#fdf0e3',
                        '& .MuiChip-icon': { color: '#b25e09' },
                      }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label="Not started"
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 700,
                        color: '#475467', backgroundColor: '#f2f4f7',
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default TutorialsPage;
